<!-- src/views/password/VerifyPasswordView.vue -->
<template>
  <base-layout>
    <page-title
      :title="t('auth.resetPassword')"
      :subtitle="t('auth.step2Subtitle')"
    />

    <!-- 步骤指示器 -->
    <a-steps :current="1" size="small" class="steps-indicator">
      <a-step :title="t('auth.step1')" />
      <a-step :title="t('auth.step2')" />
      <a-step :title="t('auth.step3')" />
    </a-steps>

    <div class="verify-container">
      <!-- 验证码提示 -->
      <div class="verify-info">
        <check-circle-outlined class="success-icon" />
        <p class="verify-text">{{ t("auth.verifyCodeSentSuccess") }}</p>
        <p class="verify-hint">{{ t("auth.verifyCodeExpires") }}</p>
      </div>

      <a-form
        :model="formState"
        @finish="onVerifyFinish"
        layout="vertical"
        class="verify-form"
      >
        <!-- 验证码输入 -->
        <form-input
          v-model="formState.verifyCode"
          :label="t('common.verifyCode')"
          name="verifyCode"
          :placeholder="t('form.enterVerifyCode')"
          :disabled="loading"
          :rules="[{ required: true, message: t('form.verifyCodeRequired') }]"
        >
          <template #prefix>
            <safety-outlined />
          </template>
        </form-input>

        <!-- 新密码 -->
        <password-input
          v-model="formState.password"
          :label="t('auth.newPassword')"
          name="password"
          :placeholder="t('form.enterPassword')"
          :rules="passwordRules"
          :disabled="loading"
          autocomplete="new-password"
        />

        <!-- 确认密码 -->
        <password-input
          v-model="formState.confirmPassword"
          :label="t('auth.confirmPassword')"
          name="confirmPassword"
          :placeholder="t('auth.enterConfirmPassword')"
          :rules="confirmPasswordRules"
          :disabled="loading"
          autocomplete="new-password"
        />

        <!-- 按钮组 -->
        <div class="button-group">
          <a-button
            type="primary"
            html-type="submit"
            :loading="loading"
            size="large"
            class="submit-button"
          >
            {{ t("auth.submit") }}
          </a-button>

          <a-button
            @click="handleResendCode"
            :disabled="loading || cooldown > 0"
            size="large"
            class="resend-button"
          >
            {{ cooldown > 0 ? `${cooldown}s` : t("auth.resendVerifyCode") }}
          </a-button>
        </div>
      </a-form>
    </div>
  </base-layout>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted, onUnmounted } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useI18n } from "vue-i18n";
import { notificationService } from "@/services/notificationService";
import { SafetyOutlined, CheckCircleOutlined } from "@ant-design/icons-vue";
import { useAuthStore } from "@/stores/auth";
import BaseLayout from "@/components/layout/BaseLayout.vue";
import PageTitle from "@/components/common/PageTitle.vue";
import FormInput from "@/components/form/FormInput.vue";
import PasswordInput from "@/components/form/PasswordInput.vue";

const router = useRouter();
const route = useRoute();
const { t } = useI18n();
const authStore = useAuthStore();
const loading = ref(false);
const cooldown = ref(0);
let cooldownTimer: number | null = null;

const formState = reactive({
  verifyCode: "",
  password: "",
  confirmPassword: "",
});

// 密码验证规则
const passwordRules = [
  { required: true, message: t("auth.passwordRequired") },
  { min: 8, message: t("auth.passwordLength") },
  {
    pattern:
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
    message: t("auth.passwordNotMeetRequirements"),
  },
];

// 确认密码验证规则
const confirmPasswordRules = computed(() => [
  { required: true, message: t("auth.confirmPasswordRequired") },
  {
    validator: (_: any, value: string) => {
      if (value && value !== formState.password) {
        return Promise.reject(t("auth.passwordNotMatch"));
      }
      return Promise.resolve();
    },
  },
]);

// 验证码提交
const onVerifyFinish = async (values: any) => {
  const username = route.query.username as string;
  if (!username) {
    notificationService.error("auth.operationFailed");
    router.replace("/password/forgot");
    return;
  }

  try {
    loading.value = true;
    await authStore.handlePasswordReset(
      username,
      values.verifyCode,
      values.password,
    );

    notificationService.success("auth.passwordSetSuccess");
    router.push({
      path: "/login",
      query: {
        username,
        message: t("auth.passwordSetSuccessDesc"),
      },
    });
  } catch (error: any) {
    console.error("Password reset error:", error);
    notificationService.error(
      error.response?.data?.message || "auth.operationFailed",
    );
  } finally {
    loading.value = false;
  }
};

// 重新发送验证码
const handleResendCode = async () => {
  if (cooldown.value > 0) return;

  const username = route.query.username as string;
  const email = sessionStorage.getItem("reset_email");
  if (!username || !email) {
    notificationService.error("auth.operationFailed");
    router.replace("/password/forgot");
    return;
  }

  try {
    loading.value = true;
    await authStore.handlePasswordResetRequest(username, email);
    notificationService.success("auth.verifyCodeSent");
    startCooldown();
  } catch (error: any) {
    console.error("Resend verify code error:", error);
    notificationService.error(
      error.response?.data?.message || "auth.operationFailed",
    );
  } finally {
    loading.value = false;
  }
};

// 冷却时间处理
const startCooldown = () => {
  cooldown.value = 60;
  cooldownTimer = window.setInterval(() => {
    if (cooldown.value > 0) {
      cooldown.value--;
    } else {
      if (cooldownTimer) {
        clearInterval(cooldownTimer);
        cooldownTimer = null;
      }
    }
  }, 1000);
};

// 组件挂载时检查必要信息
onMounted(() => {
  const username = route.query.username;
  if (!username) {
    notificationService.error("auth.operationFailed");
    router.replace("/password/forgot");
  }
});

// 组件卸载时清理定时器
onUnmounted(() => {
  if (cooldownTimer) {
    clearInterval(cooldownTimer);
  }
});
</script>

<style scoped lang="less">
@import "@/assets/styles/variables.less";

.steps-indicator {
  margin-bottom: 32px;
}

.verify-container {
  width: 100%;
  max-width: 360px;
  margin: 0 auto;
}

.verify-info {
  text-align: center;
  margin-bottom: 32px;
  padding: 24px;
  background: fade(@primary-color, 4%);
  border-radius: 8px;

  .success-icon {
    font-size: 32px;
    color: @success-color;
    margin-bottom: 16px;
  }

  .verify-text {
    font-size: @font-size-lg;
    color: @grey-85;
    margin: 0 0 8px;
  }

  .verify-hint {
    font-size: @font-size-base;
    color: @grey-45;
    margin: 0;
  }
}

.verify-form {
  width: 100%;
  max-width: 360px;
  margin: 0 auto;

  :deep(.ant-form-item) {
    margin-bottom: 24px;

    &:last-child {
      margin-bottom: 16px;
    }

    // 统一标签样式
    .ant-form-item-label {
      padding-bottom: 8px;

      label {
        color: @grey-65;
        font-size: @font-size-base;
        height: 22px;
      }
    }

    // 统一输入框样式
    .ant-input-affix-wrapper {
      padding: 8px 11px;
      height: 44px;
      border-radius: 6px;
      border: 1px solid @grey-15;
      transition: all 0.3s ease;

      &:hover {
        border-color: @primary-5;
      }

      &:focus,
      &-focused {
        border-color: @primary-color;
        box-shadow: 0 0 0 2px fade(@primary-color, 20%);
      }
    }
  }

  .verification-help {
    display: flex;
    align-items: center;
    gap: 8px;
    color: @grey-45;
    font-size: @font-size-sm;
    margin-top: 4px;
  }

  .submit-button {
    width: 100%;
    height: 44px;
    font-size: @font-size-lg;
  }

  .resend-code {
    text-align: center;
    margin-top: 16px;

    button {
      color: @primary-color;

      &:hover {
        color: @primary-5;
      }

      &:disabled {
        color: @grey-25;
      }
    }
  }
}

.button-group {
  display: flex;
  gap: 16px;
  margin-top: 32px;

  .submit-button,
  .resend-button {
    flex: 1;
    height: 44px;
  }

  .resend-button {
    color: @primary-color;
    border-color: @primary-color;

    &:disabled {
      color: @grey-25;
      border-color: @grey-15;
    }
  }
}
</style>
